import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../contexts/AuthContext";
import { AvatarPicker } from "./AvatarPicker";
import "./Auth.css";

const USERNAME_RE = /^[a-zA-Z0-9æøåÆØÅ-]{3,20}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function passwordStrength(pw: string): "weak" | "medium" | "strong" | null {
  if (!pw) return null;
  if (pw.length < 8) return "weak";
  const hasUpper = /[A-ZÆØÅ]/.test(pw);
  const hasDigit = /\d/.test(pw);
  const hasSymbol = /[^a-zA-Z0-9æøåÆØÅ]/.test(pw);
  if (pw.length >= 12 && hasUpper && (hasDigit || hasSymbol)) return "strong";
  if (pw.length >= 8 && (hasUpper || hasDigit)) return "medium";
  return "weak";
}

export function RegisterScreen() {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [avatarId, setAvatarId] = useState("avatar_1");
  const [username, setUsername] = useState("");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  function validate(): boolean {
    const e: Record<string, string> = {};

    if (!USERNAME_RE.test(username))
      e.username = "3–20 tegn – kun bokstaver, tall og bindestrek";

    if (!EMAIL_RE.test(email))
      e.email = "Ugyldig e-postadresse";

    if (password.length < 8)
      e.password = "Passord må være minst 8 tegn";

    if (password !== confirm)
      e.confirm = "Passordene er ikke like";

    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!validate()) return;

    setLoading(true);
    const error = await register({ username, email, password, avatarId });
    setLoading(false);

    if (error) {
      setFormError(error);
    } else {
      navigate("/map", { replace: true });
    }
  }

  const pwStrength = passwordStrength(password);
  const strengthLabel = { weak: "Svakt", medium: "Middels", strong: "Sterkt" } as const;

  return (
    <div className="auth-page">
      <motion.div
        className="auth-card"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 26 }}
      >
        <div className="auth-card__header">
          <h1 className="auth-card__logo">
            Golle<span>giella</span>
          </h1>
          <p className="auth-card__subtitle">Opprett konto og start reisen</p>
        </div>

        <form className="auth-card__body" onSubmit={handleSubmit} noValidate>
          {/* Avatar picker */}
          <AvatarPicker value={avatarId} onChange={setAvatarId} />

          <hr className="auth-divider" />

          {formError && <p className="auth-form-error">⚠ {formError}</p>}

          {/* Username */}
          <div className="auth-field">
            <label className="auth-label" htmlFor="reg-username">Brukernavn</label>
            <input
              id="reg-username"
              className={`auth-input${errors.username ? " auth-input--error" : ""}`}
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              placeholder="f.eks. jovnna_99"
              maxLength={20}
            />
            {errors.username && <p className="auth-error-text">✕ {errors.username}</p>}
          </div>

          {/* Email */}
          <div className="auth-field">
            <label className="auth-label" htmlFor="reg-email">E-post</label>
            <input
              id="reg-email"
              className={`auth-input${errors.email ? " auth-input--error" : ""}`}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="du@eksempel.no"
            />
            {errors.email && <p className="auth-error-text">✕ {errors.email}</p>}
          </div>

          {/* Password */}
          <div className="auth-field">
            <label className="auth-label" htmlFor="reg-password">Passord</label>
            <input
              id="reg-password"
              className={`auth-input${errors.password ? " auth-input--error" : ""}`}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="Minst 8 tegn"
            />
            {/* Strength indicator */}
            {pwStrength && (
              <div
                className={`password-strength password-strength--${pwStrength}`}
                aria-label={`Passordstyrke: ${strengthLabel[pwStrength]}`}
              />
            )}
            {errors.password && <p className="auth-error-text">✕ {errors.password}</p>}
          </div>

          {/* Confirm password */}
          <div className="auth-field">
            <label className="auth-label" htmlFor="reg-confirm">Bekreft passord</label>
            <input
              id="reg-confirm"
              className={`auth-input${errors.confirm ? " auth-input--error" : ""}`}
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              placeholder="Gjenta passordet"
            />
            {errors.confirm && <p className="auth-error-text">✕ {errors.confirm}</p>}
          </div>

          <button
            type="submit"
            className="auth-submit"
            disabled={loading}
          >
            {loading ? "Oppretter konto…" : "Opprett konto"}
          </button>
        </form>

        <div className="auth-footer">
          Har du allerede konto?{" "}
          <Link to="/login">Logg inn</Link>
        </div>
      </motion.div>
    </div>
  );
}
